import React from 'react';
import { mostraMensagemErro } from '../../helpers/Notificacoes';
import { IndicadoresGraficosService } from '../../services/IndicadoresGraficosService';
import {
  IndicadorChart,
  IndicadorItensMaisVendidosChart,
  IndicadorStatusPedidosChart,
  IndicadorTopClientesChart,
  IndicadorTopRepresentantesChart,
} from './IndicadoresGraficos.types';

// ── Tipos internos de API ──────────────────────────────────────────────────────

type IndicadorApi = {
  data: string;
  qtd_pedidos: number | string;
  total_faturado: number | string;
};

type IndicadorItensMaisVendidosApi = {
  codItem?: string | number;
  cod_item?: string | number;
  codigo_item?: string | number;
  item?: string;
  descricao?: string;
  nome_item?: string;
  nome?: string;
  qtd_vendida?: number | string;
  qtd_total?: number | string;
  quantidade?: number | string;
  qtd?: number | string;
  qtd_pedidos?: number | string;
  total_faturado?: number | string;
  total_vendido?: number | string;
  valor_total?: number | string;
  valor?: number | string;
  total?: number | string;
};

type IndicadorStatusPedidosApi = {
  statusPedido?: string;
  status_pedido?: string;
  status?: string;
  quantidadePedidos?: number | string;
  quantidade_pedidos?: number | string;
  quantidade?: number | string;
  percentual?: number | string;
  perc?: number | string;
  total?: number | string;
  total_faturado?: number | string;
  totalFaturado?: number | string;
  valor_total?: number | string;
  valorTotal?: number | string;
  total_vendido?: number | string;
  totalVendido?: number | string;
  valor?: number | string;
};

type IndicadorTopClientesApi = {
  id_agente?: string | number;
  nome_cliente?: string;
  total_gasto?: number | string;
  qtd_pedidos?: number | string;
};

type IndicadorTopRepresentantesApi = {
  nome_representante?: string;
  nomeRepresentante?: string;
  nome_agente?: string;
  nomeAgente?: string;
  nome?: string;
  representante?: string;
  total_gasto?: number | string;
  totalGasto?: number | string;
  total_faturado?: number | string;
  totalFaturado?: number | string;
  valor_total?: number | string;
  valorTotal?: number | string;
  total_vendido?: number | string;
  totalVendido?: number | string;
  valor?: number | string;
  total?: number | string;
  qtd_pedidos?: number | string;
  qtdPedidos?: number | string;
  quantidade_pedidos?: number | string;
  quantidadePedidos?: number | string;
  quantidade?: number | string;
  percentual?: number | string;
};

// ── Tipos de parâmetros ───────────────────────────────────────────────────────

export type CarregarIndicadoresParams = {
  periodo: PeriodoFiltro;
  idEmpresa: string | number;
  idAgenteLogado: string | number;
  idAgenteFiltro?: string;
  filtrarPorAgenteRepresentante?: boolean;
  indicadoresService: IndicadoresGraficosService;
  setDados: React.Dispatch<React.SetStateAction<IndicadorChart[]>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export type CarregarItensMaisVendidosParams = {
  periodo: PeriodoFiltro;
  idEmpresa: string | number;
  idAgenteLogado: string | number;
  idAgenteFiltro?: string;
  filtrarPorAgenteRepresentante?: boolean;
  limiteRanking?: number;
  indicadoresService: IndicadoresGraficosService;
  setDados: React.Dispatch<React.SetStateAction<IndicadorItensMaisVendidosChart[]>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export type CarregarStatusPedidosParams = {
  periodo: PeriodoFiltro;
  idEmpresa: string | number;
  idAgenteLogado: string | number;
  idAgenteFiltro?: string;
  filtrarPorAgenteRepresentante?: boolean;
  indicadoresService: IndicadoresGraficosService;
  setDados: React.Dispatch<React.SetStateAction<IndicadorStatusPedidosChart[]>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export type CarregarTopClientesParams = {
  periodo: PeriodoFiltro;
  idEmpresa: string | number;
  idAgenteLogado: string | number;
  idAgenteFiltro?: string;
  filtrarPorAgenteRepresentante?: boolean;
  indicadoresService: IndicadoresGraficosService;
  setDados: React.Dispatch<React.SetStateAction<IndicadorTopClientesChart[]>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export type CarregarTopRepresentantesParams = {
  periodo: PeriodoFiltro;
  idEmpresa: string | number;
  idAgenteLogado: string | number;
  idAgenteFiltro?: string;
  filtrarPorAgenteRepresentante?: boolean;
  indicadoresService: IndicadoresGraficosService;
  setDados: React.Dispatch<React.SetStateAction<IndicadorTopRepresentantesChart[]>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

// ── Constantes ────────────────────────────────────────────────────────────────

const LIMITE_RANKING_ITENS = 15;

export type PeriodoFiltro =
  | 'ultima_semana'
  | 'ultimo_mes'
  | 'ultimos_3_meses'
  | 'ultimos_6_meses'
  | 'ultimos_12_meses';

// ── Helpers de formatação ─────────────────────────────────────────────────────

const NOMES_DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const NOMES_MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function parseDataISO(dataISO: string): Date {
  // Usa UTC para evitar issues de timezone
  const [ano, mes, dia] = dataISO.split('-').map(Number);
  const data = new Date(ano, mes - 1, dia);
  data.setHours(0, 0, 0, 0);
  return data;
}

function dataParaISO(date: Date): string {
  const ano = date.getFullYear();
  const mes = String(date.getMonth() + 1).padStart(2, '0');
  const dia = String(date.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
}

function inicioDaSemana(date: Date): Date {
  const inicio = new Date(date);
  inicio.setHours(0, 0, 0, 0);
  // Usa segunda-feira (1) como início de semana em vez de domingo (0)
  // Se for domingo (0), volta 6 dias; se for segunda (1), volta 1 dia, etc.
  const dia = inicio.getDay();
  const diasAVoltar = dia === 0 ? 6 : dia - 1;
  inicio.setDate(inicio.getDate() - diasAVoltar);
  return inicio;
}

type DadoBruto = { dataISO: string; qtdPedidos: number; totalFaturado: number };

export function agruparDadosPorPeriodo(
  dadosBrutos: DadoBruto[],
  periodo: PeriodoFiltro
): { data: string; qtdPedidos: number; totalFaturado: number }[] {
  const porDia = new Map<string, { qtdPedidos: number; totalFaturado: number }>();
  let dataMaisRecente: Date | null = null;

  for (const item of dadosBrutos) {
    const dataAtual = parseDataISO(item.dataISO);
    const chave = item.dataISO;
    const existente = porDia.get(chave);
    if (existente) {
      existente.qtdPedidos += item.qtdPedidos;
      existente.totalFaturado += item.totalFaturado;
    } else {
      porDia.set(chave, { qtdPedidos: item.qtdPedidos, totalFaturado: item.totalFaturado });
    }

    if (!dataMaisRecente || dataAtual > dataMaisRecente) {
      dataMaisRecente = dataAtual;
    }
  }

  if (periodo === 'ultima_semana') {
    const referencia = dataMaisRecente ?? new Date();
    // Últimos 7 dias: começa 6 dias antes da data mais recente
    const seteDiasAtras = new Date(referencia);
    seteDiasAtras.setDate(referencia.getDate() - 6);

    const resultado: { data: string; qtdPedidos: number; totalFaturado: number }[] = [];
    for (let i = 0; i < 7; i += 1) {
      const d = new Date(seteDiasAtras);
      d.setDate(seteDiasAtras.getDate() + i);

      const mes = d.getMonth() + 1;
      const dia = d.getDate();
      const chave = dataParaISO(d);
      const valores = porDia.get(chave) || { qtdPedidos: 0, totalFaturado: 0 };
      const label = `${NOMES_DIAS_SEMANA[d.getDay()]} ${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}`;

      resultado.push({
        data: label,
        qtdPedidos: valores.qtdPedidos,
        totalFaturado: valores.totalFaturado,
      });
    }

    return resultado;
  }

  if (periodo === 'ultimo_mes') {
    const referencia = dataMaisRecente ?? new Date();
    const inicioPeriodo = new Date(referencia);
    inicioPeriodo.setDate(referencia.getDate() - 29);

    const resultado: { data: string; qtdPedidos: number; totalFaturado: number }[] = [];
    for (let i = 0; i < 30; i += 1) {
      const d = new Date(inicioPeriodo);
      d.setDate(inicioPeriodo.getDate() + i);
      const chave = dataParaISO(d);
      const valores = porDia.get(chave) || { qtdPedidos: 0, totalFaturado: 0 };

      resultado.push({
        data: String(d.getDate()).padStart(2, '0'),
        qtdPedidos: valores.qtdPedidos,
        totalFaturado: valores.totalFaturado,
      });
    }

    return resultado;
  }

  if (periodo === 'ultimos_3_meses') {
    const mapaSemanal = new Map<string, { qtdPedidos: number; totalFaturado: number; dataInicio: Date }>();
    
    // Processa todos os dados brutos
    for (const item of dadosBrutos) {
      try {
        const dataAtual = parseDataISO(item.dataISO);
        const inicioSemanaAtual = inicioDaSemana(dataAtual);
        const chave = dataParaISO(inicioSemanaAtual);
        const existente = mapaSemanal.get(chave);
        
        if (existente) {
          existente.qtdPedidos += Number(item.qtdPedidos || 0);
          existente.totalFaturado += Number(item.totalFaturado || 0);
        } else {
          mapaSemanal.set(chave, {
            qtdPedidos: Number(item.qtdPedidos || 0),
            totalFaturado: Number(item.totalFaturado || 0),
            dataInicio: inicioSemanaAtual,
          });
        }
      } catch (erro) {
        console.warn(`Erro ao processar data ${item.dataISO}:`, erro);
      }
    }

    // Retorna semanas ordenadas, filtrando aquelas com dados
    return Array.from(mapaSemanal.entries())
      .filter(([, val]) => val.qtdPedidos > 0 || val.totalFaturado > 0)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, val]) => ({
        data: `Semana ${String(val.dataInicio.getDate()).padStart(2, '0')}/${String(val.dataInicio.getMonth() + 1).padStart(2, '0')}`,
        qtdPedidos: val.qtdPedidos,
        totalFaturado: val.totalFaturado,
      }));
  }

  // ultimos_6_meses | ultimos_12_meses → agrupa por mês
  const mapa = new Map<string, { qtdPedidos: number; totalFaturado: number; mes: number; ano: number }>();
  for (const item of dadosBrutos) {
    try {
      const [ano, mes] = item.dataISO.split('-').map(Number);
      const chave = `${ano}-${String(mes).padStart(2, '0')}`;
      const existente = mapa.get(chave);
      if (existente) {
        existente.qtdPedidos += Number(item.qtdPedidos || 0);
        existente.totalFaturado += Number(item.totalFaturado || 0);
      } else {
        mapa.set(chave, { 
          qtdPedidos: Number(item.qtdPedidos || 0), 
          totalFaturado: Number(item.totalFaturado || 0), 
          mes, 
          ano 
        });
      }
    } catch (erro) {
      console.warn(`Erro ao processar data ${item.dataISO}:`, erro);
    }
  }
  
  return Array.from(mapa.entries())
    .filter(([, val]) => val.qtdPedidos > 0 || val.totalFaturado > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, val]) => ({
      data: `${NOMES_MESES[val.mes - 1]}/${String(val.ano).slice(2)}`,
      qtdPedidos: val.qtdPedidos,
      totalFaturado: val.totalFaturado,
    }));
}

export function formatarMoeda(valor: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(valor || 0);
}

export function formatarPeriodoLabel(periodo: PeriodoFiltro): string {
  switch (periodo) {
    case 'ultima_semana':
      return 'Última semana';
    case 'ultimo_mes':
      return 'Último mês';
    case 'ultimos_3_meses':
      return 'Últimos 3 meses';
    case 'ultimos_6_meses':
      return 'Últimos 6 meses';
    case 'ultimos_12_meses':
      return 'Últimos 12 meses';
    default:
      return 'Período selecionado';
  }
}

// ── Helpers de normalização ───────────────────────────────────────────────────

function normalizarNomeItem(item: IndicadorItensMaisVendidosApi, indice: number): string {
  const nome = String(item.item || item.descricao || item.nome_item || item.nome || '').trim();
  return nome || `Item ${indice + 1}`;
}

function normalizarCodigoItem(item: IndicadorItensMaisVendidosApi, indice: number): string {
  const codigo = String(item.codItem || item.cod_item || item.codigo_item || '').trim();
  return codigo || `SEM-COD-${indice + 1}`;
}

function normalizarNomeStatus(item: IndicadorStatusPedidosApi): string {
  const nome = String(item.statusPedido || item.status_pedido || item.status || '').trim();
  return nome || 'Nao informado';
}

function normalizarNomeCliente(item: IndicadorTopClientesApi, indice: number): string {
  const nome = String(item.nome_cliente || '').trim();

  return nome || `Cliente ${indice + 1}`;
}

function normalizarNomeRepresentante(item: IndicadorTopRepresentantesApi, indice: number): string {
  const nome = String(
    item.nome_representante
    || item.nomeRepresentante
    || item.nome_agente
    || item.nomeAgente
    || item.representante
    || item.nome
    || ''
  ).trim();

  return nome || `Representante ${indice + 1}`;
}

function normalizarNumero(valor: number | string | undefined): number {
  if (typeof valor === 'number') {
    return Number.isFinite(valor) ? valor : 0;
  }

  if (typeof valor !== 'string') {
    return 0;
  }

  const valorLimpo = valor
    .trim()
    .replace(/\s/g, '')
    .replace(/R\$/g, '')
    .replace(/\.(?=\d{3}(?:\D|$))/g, '')
    .replace(',', '.');

  const numero = Number(valorLimpo);
  return Number.isFinite(numero) ? numero : 0;
}

function criarParametrosIndicadores({
  idEmpresa,
  periodo,
  idAgenteLogado,
  idAgenteFiltro,
  filtrarPorAgenteRepresentante,
}: {
  idEmpresa: string | number;
  periodo: PeriodoFiltro;
  idAgenteLogado: string | number;
  idAgenteFiltro?: string;
  filtrarPorAgenteRepresentante?: boolean;
}): URLSearchParams {
  const parametros = new URLSearchParams();
  const filtroAtivo = Boolean(filtrarPorAgenteRepresentante && idAgenteFiltro);
  const agenteRepresentante = filtroAtivo ? idAgenteFiltro : String(idAgenteLogado);

  if (idEmpresa !== undefined && idEmpresa !== null && String(idEmpresa).trim() !== '') {
    parametros.set('idEmpresa', String(idEmpresa));
  }

  if (agenteRepresentante && String(agenteRepresentante).trim() !== '') {
    parametros.set('agenteRepresentante', String(agenteRepresentante));
  }

  parametros.set('periodo', periodo);
  parametros.set('filtrarPorAgenteRepresentante', String(filtroAtivo));

  return parametros;
}

// ── Loaders ───────────────────────────────────────────────────────────────────

export async function carregarIndicadores({
  periodo,
  idEmpresa,
  idAgenteLogado,
  idAgenteFiltro,
  filtrarPorAgenteRepresentante,
  indicadoresService,
  setDados,
  setIsLoading,
}: CarregarIndicadoresParams) {
  if (!periodo) {
    mostraMensagemErro('Informe o periodo para buscar os indicadores.');
    return;
  }

  setIsLoading(true);

  try {
    const parametros = criarParametrosIndicadores({
      idEmpresa,
      periodo,
      idAgenteLogado,
      idAgenteFiltro,
      filtrarPorAgenteRepresentante,
    });
    const response = await indicadoresService.get(
      `/v1/indicadores-graficos?${parametros.toString()}`
    );

    if (response.codigo !== 200) {
      mostraMensagemErro(response.mensagem || 'Erro ao buscar indicadores.');
      setDados([]);
      return;
    }

    const statusBruto = response.status;
    const status = (
      Array.isArray(statusBruto)
        ? statusBruto
        : statusBruto?.pedidos || statusBruto?.lista || statusBruto?.data || statusBruto?.resultado || []
    ) as IndicadorApi[];

    const dadosFormatados = status.map((item) => ({
      dataISO: String(item.data).slice(0, 10),
      qtdPedidos: Number(item.qtd_pedidos || 0),
      totalFaturado: Number(item.total_faturado || 0),
    }));

    setDados(agruparDadosPorPeriodo(dadosFormatados, periodo));
  } catch (error: any) {
    mostraMensagemErro(error.message || 'Erro inesperado ao buscar indicadores.');
    setDados([]);
  } finally {
    setIsLoading(false);
  }
}

export async function carregarItensMaisVendidos({
  periodo,
  idEmpresa,
  idAgenteLogado,
  idAgenteFiltro,
  filtrarPorAgenteRepresentante,
  limiteRanking = 10,
  indicadoresService,
  setDados,
  setIsLoading,
}: CarregarItensMaisVendidosParams) {
  if (!periodo) {
    mostraMensagemErro('Informe o periodo para buscar os indicadores.');
    return;
  }

  setIsLoading(true);

  try {
    const parametros = criarParametrosIndicadores({
      idEmpresa,
      periodo,
      idAgenteLogado,
      idAgenteFiltro,
      filtrarPorAgenteRepresentante,
    });
    const response = await indicadoresService.get(
      `/v1/indicadores-graficos/itens-mais-vendidos?${parametros.toString()}`
    );

    if (response.codigo !== 200) {
      mostraMensagemErro(response.mensagem || 'Erro ao buscar itens mais vendidos.');
      setDados([]);
      return;
    }

    const statusBruto = response.status;
    const listaItens = Array.isArray(statusBruto)
      ? statusBruto
      : statusBruto?.itens || [];

    const dadosFormatados = (listaItens as IndicadorItensMaisVendidosApi[])
      .map((item, indice) => {
        const nomeItem = normalizarNomeItem(item, indice);
        const codigoItem = normalizarCodigoItem(item, indice);

        return {
          codigoItem,
          nomeItem,
          quantidadeVendida: Number(item.qtd_total || item.qtd_vendida || item.quantidade || item.qtd || item.qtd_pedidos || 0),
          rotuloRanking: `${codigoItem}`,
        };
      })
      .sort((itemA, itemB) => itemB.quantidadeVendida - itemA.quantidadeVendida)
      .slice(0, limiteRanking);

    setDados(dadosFormatados);
  } catch (error: any) {
    mostraMensagemErro(error.message || 'Erro inesperado ao buscar itens mais vendidos.');
    setDados([]);
  } finally {
    setIsLoading(false);
  }
}

export async function carregarStatusPedidos({
  periodo,
  idEmpresa,
  idAgenteLogado,
  idAgenteFiltro,
  filtrarPorAgenteRepresentante,
  indicadoresService,
  setDados,
  setIsLoading,
}: CarregarStatusPedidosParams) {
  if (!periodo) {
    setDados([]);
    return;
  }

  setIsLoading(true);

  try {
    const parametros = criarParametrosIndicadores({
      idEmpresa,
      periodo,
      idAgenteLogado,
      idAgenteFiltro,
      filtrarPorAgenteRepresentante,
    });
    const response = await indicadoresService.get(
      `/v1/indicadores-graficos/status-pedidos?${parametros.toString()}`
    );

    if (response.codigo !== 200) {
      setDados([]);
      return;
    }

    const statusBruto = response.status;
    const listaStatus = Array.isArray(statusBruto)
      ? statusBruto
      : statusBruto?.listaStatus || statusBruto?.status || [];

    const itens = (listaStatus as IndicadorStatusPedidosApi[]).map((item) => ({
      statusPedido: normalizarNomeStatus(item),
      quantidadePedidos: Number(item.quantidadePedidos || item.quantidade_pedidos || item.quantidade || 0),
      percentual: Number(item.percentual || item.perc || 0),
      total: normalizarNumero(
        item.total
        || item.total_faturado
        || item.totalFaturado
        || item.valor_total
        || item.valorTotal
        || item.total_vendido
        || item.totalVendido
        || item.valor
      ),
    }));

    const totalItens = itens.reduce((acc, item) => acc + item.quantidadePedidos, 0);

    const dadosFormatados = itens
      .map((item) => ({
        ...item,
        percentual: item.percentual > 0
          ? item.percentual
          : totalItens > 0
            ? (item.quantidadePedidos / totalItens) * 100
            : 0,
      }))
      .sort((itemA, itemB) => itemB.quantidadePedidos - itemA.quantidadePedidos);

    setDados(dadosFormatados);
  } catch {
    setDados([]);
  } finally {
    setIsLoading(false);
  }
}

export async function carregarTopClientes({
  periodo,
  idEmpresa,
  idAgenteLogado,
  idAgenteFiltro,
  filtrarPorAgenteRepresentante,
  indicadoresService,
  setDados,
  setIsLoading,
}: CarregarTopClientesParams) {
  if (!periodo) {
    mostraMensagemErro('Informe o periodo para buscar os indicadores.');
    setDados([]);
    return;
  }

  setIsLoading(true);

  try {
    const endpointTopClientes = '/v1/indicadores-graficos/top-clientes';
    const parametros = criarParametrosIndicadores({
      idEmpresa,
      periodo,
      idAgenteLogado,
      idAgenteFiltro,
      filtrarPorAgenteRepresentante,
    });

    const sufixoQuery = parametros.toString();
    const caminho = sufixoQuery ? `${endpointTopClientes}?${sufixoQuery}` : endpointTopClientes;

    const response = await indicadoresService.get(caminho);

    if (response.codigo !== 200) {
      mostraMensagemErro(response.mensagem || 'Erro ao buscar top clientes.');
      setDados([]);
      return;
    }

    const statusBruto = response.status;
    const listaClientes = Array.isArray(statusBruto)
      ? statusBruto
      : statusBruto?.clientes || statusBruto?.listaClientes || statusBruto?.topClientes || [];

    const itensBrutos = (listaClientes as IndicadorTopClientesApi[]).map((item, indice) => ({
      nomeCliente: normalizarNomeCliente(item, indice),
      totalGasto: Number(item.total_gasto || 0),
      qtdPedidos: Number(item.qtd_pedidos || 0),
      percentual: 0,
    }));

    const totalPedidosPeriodo = itensBrutos.reduce((acc, item) => acc + item.qtdPedidos, 0);

    const dadosFormatados = itensBrutos
      .map((item) => ({
        ...item,
        percentual: item.percentual > 0
          ? item.percentual
          : totalPedidosPeriodo > 0
            ? (item.qtdPedidos / totalPedidosPeriodo) * 100
            : 0,
        rotuloRanking: item.nomeCliente,
      }))
      .sort((itemA, itemB) => {
        const diferencaQtdPedidos = itemB.qtdPedidos - itemA.qtdPedidos;

        if (diferencaQtdPedidos !== 0) {
          return diferencaQtdPedidos;
        }

        return itemB.totalGasto - itemA.totalGasto;
      })
      .slice(0, LIMITE_RANKING_ITENS);

    setDados(dadosFormatados);
  } catch {
    setDados([]);
  } finally {
    setIsLoading(false);
  }
}

export async function carregarTopRepresentantes({
  periodo,
  idEmpresa,
  idAgenteLogado,
  idAgenteFiltro,
  filtrarPorAgenteRepresentante,
  indicadoresService,
  setDados,
  setIsLoading,
}: CarregarTopRepresentantesParams) {
  if (!periodo) {
    mostraMensagemErro('Informe o periodo para buscar os indicadores.');
    setDados([]);
    return;
  }

  setIsLoading(true);

  try {
    const endpointTopRepresentantes = '/v1/indicadores-graficos/top-representantes';
    const parametros = criarParametrosIndicadores({
      idEmpresa,
      periodo,
      idAgenteLogado,
      idAgenteFiltro,
      filtrarPorAgenteRepresentante,
    });

    const sufixoQuery = parametros.toString();
    const caminho = sufixoQuery ? `${endpointTopRepresentantes}?${sufixoQuery}` : endpointTopRepresentantes;

    const response = await indicadoresService.get(caminho);

    if (response.codigo !== 200) {
      mostraMensagemErro(response.mensagem || 'Erro ao buscar top representantes.');
      setDados([]);
      return;
    }

    const statusBruto = response.status;
    const listaRepresentantes = Array.isArray(statusBruto)
      ? statusBruto
      : statusBruto?.representantes
      || statusBruto?.listaRepresentantes
      || statusBruto?.topRepresentantes
      || [];

    const itensBrutos = (listaRepresentantes as IndicadorTopRepresentantesApi[]).map((item, indice) => ({
      nomeRepresentante: normalizarNomeRepresentante(item, indice),
      totalGasto: normalizarNumero(
        item.total_gasto
        || item.totalGasto
        || item.total_faturado
        || item.totalFaturado
        || item.valor_total
        || item.valorTotal
        || item.total_vendido
        || item.totalVendido
        || item.valor
        || item.total
      ),
      qtdPedidos: normalizarNumero(
        item.qtd_pedidos
        || item.qtdPedidos
        || item.quantidade_pedidos
        || item.quantidadePedidos
        || item.quantidade
      ),
      percentual: normalizarNumero(item.percentual),
    }));

    const totalPeriodo = itensBrutos.reduce((acc, item) => acc + item.totalGasto, 0);

    const dadosFormatados = itensBrutos
      .map((item) => ({
        ...item,
        percentual: item.percentual > 0
          ? item.percentual
          : totalPeriodo > 0
            ? (item.totalGasto / totalPeriodo) * 100
            : 0,
        rotuloRanking: item.nomeRepresentante,
      }))
      .sort((itemA, itemB) => {
        const diferencaQtdPedidos = itemB.qtdPedidos - itemA.qtdPedidos;

        if (diferencaQtdPedidos !== 0) {
          return diferencaQtdPedidos;
        }

        return itemB.totalGasto - itemA.totalGasto;
      })
      .slice(0, LIMITE_RANKING_ITENS);

    setDados(dadosFormatados);
  } catch {
    setDados([]);
  } finally {
    setIsLoading(false);
  }
}

